import { useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { features } from '@/data/features';
import { toneForCategory } from '@/data/categoryTone';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { FilterChips } from '@/components/ui/FilterChips';

const categories = Array.from(new Set(features.map((f) => f.category))).sort();

export function FeaturesPage() {
  const [category, setCategory] = useState<string | null>(null);

  const visible = useMemo(
    () => (category ? features.filter((f) => f.category === category) : features),
    [category],
  );

  return (
    <div className="px-4 md:px-8 py-12 max-w-5xl mx-auto">
      <h1 className="text-h1 text-text-primary mb-2">Features</h1>
      <p className="text-body text-text-secondary max-w-2xl mb-6">
        Everything below describes what the Dem bot actually does today, sourced directly from a
        full source-code audit — not aspirational copy. Where a feature has a known limitation,
        it's disclosed here too.
      </p>

      <div className="mb-6">
        <FilterChips options={categories} selected={category} onSelect={setCategory} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {visible.map((feature) => (
          <Card key={feature.id}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <h3 className="text-h3 text-text-primary">{feature.name}</h3>
              <Badge tone={toneForCategory(feature.category)}>{feature.category}</Badge>
            </div>
            <p className="text-small text-text-secondary">{feature.summary}</p>
            {feature.limitation ? (
              <div className="flex items-start gap-2 mt-3 pt-3 border-t border-border-subtle">
                <AlertTriangle
                  size={14}
                  className="text-warning shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <p className="text-small text-text-tertiary">{feature.limitation}</p>
              </div>
            ) : null}
          </Card>
        ))}
      </div>
    </div>
  );
}
